import {
  DATE_REGEX,
  FIELD_MAX_LENGTHS,
  FIELD_RANGES,
  INDUSTRY_GROUPS,
  PRIORITY_VALUES,
  RECRUITMENT_TYPE_VALUES,
  VALIDATION_MESSAGES,
} from 'shared'
import { z } from 'zod'
import { validateForm } from '@/lib/form-validation'

export const inquiryFormSchema = z
  .object({
    overview: z
      .string()
      .trim()
      .max(FIELD_MAX_LENGTHS.INQUIRY_OVERVIEW, VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.INQUIRY_OVERVIEW))
      .nullable()
      .optional(),
    requirements: z
      .string()
      .trim()
      .min(1, VALIDATION_MESSAGES.REQUIRED)
      .max(
        FIELD_MAX_LENGTHS.INQUIRY_REQUIREMENTS,
        VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.INQUIRY_REQUIREMENTS),
      ),
    startOn: z.string().regex(DATE_REGEX, '日付の形式が正しくありません').nullable().optional(),
    endOn: z.string().regex(DATE_REGEX, '日付の形式が正しくありません').nullable().optional(),
    estimatedAmount: z
      .number()
      .nonnegative()
      .max(
        FIELD_MAX_LENGTHS.INQUIRY_ESTIMATED_AMOUNT,
        VALIDATION_MESSAGES.maxAmount(FIELD_MAX_LENGTHS.INQUIRY_ESTIMATED_AMOUNT),
      )
      .nullable()
      .optional(),
    industryName: z.enum(INDUSTRY_GROUPS, { error: VALIDATION_MESSAGES.INVALID_SELECT }).nullable().optional(),
    station: z
      .string()
      .max(FIELD_MAX_LENGTHS.NAME_MEDIUM, VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.NAME_MEDIUM))
      .nullable()
      .optional(),
    priority: z.enum(PRIORITY_VALUES).optional(),
    recruitmentType: z.enum(RECRUITMENT_TYPE_VALUES).nullable().optional(),
    inquiryCloseReason: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.startOn && data.endOn) {
        return data.startOn <= data.endOn
      }
      return true
    },
    { message: '開始日は終了日以前にしてください', path: ['endOn'] },
  )

// --- アサイン ---
const assignCommonFields = {
  unitPrice: z
    .number()
    .nonnegative()
    .max(FIELD_RANGES.UNIT_PRICE_MAX, VALIDATION_MESSAGES.maxAmount(FIELD_RANGES.UNIT_PRICE_MAX))
    .nullable()
    .optional(),
  assignPersonMonth: z
    .number()
    .min(FIELD_RANGES.ASSIGN_PERSON_MONTH_MIN)
    .max(FIELD_RANGES.ASSIGN_PERSON_MONTH_MAX, VALIDATION_MESSAGES.maxAmount(FIELD_RANGES.ASSIGN_PERSON_MONTH_MAX))
    .nullable()
    .optional(),
  notes: z
    .string()
    .max(FIELD_MAX_LENGTHS.INQUIRY_NOTES, VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.INQUIRY_NOTES))
    .nullable()
    .optional(),
}

export function createInquiryAssignFormSchema(_isEdit: boolean) {
  return z.object({
    ...assignCommonFields,
    assignedOn: z.string({ error: 'アサイン開始月は必須です' }).regex(DATE_REGEX, '日付の形式が正しくありません'),
  })
}

// --- コーディネーション ---
export const inquiryCoordinationSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, VALIDATION_MESSAGES.REQUIRED)
    .max(
      FIELD_MAX_LENGTHS.INQUIRY_COORDINATION_CONTENT,
      VALIDATION_MESSAGES.maxLength(FIELD_MAX_LENGTHS.INQUIRY_COORDINATION_CONTENT),
    ),
})

/**
 * 調整状況コンテンツが有効かどうかをチェック（送信ボタンのdisabled用）
 */
export function isCoordinationContentValid(value: string): boolean {
  return inquiryCoordinationSchema.safeParse({ content: value }).success
}

/**
 * 調整状況入力中のバリデーション（最大文字数）
 */
export function getCoordinationContentMaxLengthError(value: string): string | null {
  if (value.trim().length === 0) return null
  const { errors } = validateForm(inquiryCoordinationSchema, { content: value })
  return errors.content ?? null
}
